import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Container, Row, Col } from 'styled-bootstrap-grid';
import { MdKeyboardArrowRight } from 'react-icons/md';

import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

import Header from '../../containers/Header';
import Footer from '../../containers/Footer';
import Breadcrumbs from '../../containers/Breadcrumbs';

import Card from '../../components/Card';
import Datatable from '../../components/Datatable';
import Button from '../../components/Button';
import DropdownButton from '../../components/DropdownButton';
import Badge from '../../components/Badge';

interface productData {
  id: string;
  created_at: string;
  name: string;
  amount: string;
  duration_in_months: number;
  wordings_per_month: number;
  active: boolean;
}

interface columnData {
  label: string;
  field:
    | 'id'
    | 'created_at'
    | 'name'
    | 'amount'
    | 'duration_in_months'
    | 'wordings_per_month'
    | 'active';
  transform?(
    value: string | boolean | number,
    row?: productData,
  ): string | React.ReactElement;
}

const Product: React.FC = () => {
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [maxPages, setMaxPages] = useState(0);

  const [showingFrom, setShowingFrom] = useState(0);
  const [showingTo, setShowingTo] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [data, setData] = useState<productData[]>([]);

  const columns = useMemo<columnData[]>(
    () => [
      {
        label: 'Cadastro',
        field: 'created_at',
        transform: (created_at: string) => {
          const formattedDate = format(
            parseISO(created_at),
            "dd/MM/yyyy 'às' HH:mm",
            { locale: ptBR },
          );

          return formattedDate;
        },
      },
      {
        label: 'Nome',
        field: 'name',
      },
      {
        label: 'Valor',
        field: 'amount',
      },
      {
        label: 'Duração',
        field: 'duration_in_months',
        transform: duration_in_months => {
          const monthPlural = duration_in_months > 1 ? 'meses' : 'mês';

          return `${duration_in_months} ${monthPlural}`;
        },
      },
      {
        label: 'Créditos',
        field: 'wordings_per_month',
        transform: wordings_per_month => `${wordings_per_month} por mês`,
      },
      {
        label: 'Status',
        field: 'active',
        transform: (active: boolean) => (
          <Badge
            title={active ? 'Ativo' : 'Inativo'}
            color={active ? 'success' : 'error'}
          />
        ),
      },
      {
        label: '',
        field: 'id',
        transform: id => (
          <DropdownButton>
            <ul>
              <li>
                <Link to={`/planos/visualizar/${id}`}>
                  <MdKeyboardArrowRight size={18} />
                  Visualizar
                </Link>
              </li>
            </ul>
          </DropdownButton>
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        const response = await api.get('/products', {
          params: { pageIndex, search },
        });

        setMaxPages(response.data.maxPages);
        setShowingFrom(response.data.showingFrom);
        setShowingTo(response.data.showingTo);
        setTotalRecords(response.data.totalRecords);
        setData(response.data.records);
      } catch (error) {
        // console.log(error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [pageIndex, search]);

  return (
    <>
      <Header />

      <Container style={{ flex: 1 }}>
        <Breadcrumbs title="Planos" items={[{ title: 'Planos' }]}>
          <Button
            color="secondary"
            onClick={() => history.push('/planos/cadastrar')}
          >
            Cadastrar plano
          </Button>
        </Breadcrumbs>

        <Row>
          <Col md={12}>
            <Card>
              <Datatable
                loading={loading}
                columns={columns}
                showingTo={showingTo}
                showingFrom={showingFrom}
                totalRecords={totalRecords}
                pageIndex={pageIndex}
                maxPages={maxPages}
                setPageIndex={(value: number) => setPageIndex(value)}
                setSearch={(value: string) => setSearch(value)}
              >
                {data.map(row => (
                  <tr key={row.id}>
                    {columns.map(column => (
                      <td key={column.field}>
                        {column.transform
                          ? column.transform(row[column.field], row)
                          : row[column.field]}
                      </td>
                    ))}
                  </tr>
                ))}
                {!data.length && (
                  <tr>
                    <td colSpan={columns.length}>Nenhum plano encontrado</td>
                  </tr>
                )}
              </Datatable>
            </Card>
          </Col>
        </Row>
      </Container>

      <Footer />
    </>
  );
};

export default Product;
